// WALLET REGISTER (UI rebuild batch 5) — every address the books know, across every party, and what each one is: in use, held by screening, retired
// (still watched) or archived (not watched). His rulings (db/0064): archive stops the chain reader, KYT and Energy reading the address; nothing is ever
// deleted; nothing retired comes back — so Retire and Archive go one way, each asks for a reason, and each says what it does before it is taken.
//
// BUILT FROM Screens' Astra-approved reformat (branch screens/wallet-register, 538a2ff, round 6): its logic is components/v2/wallets/ — view.ts (the
// views, the filters, the arrangement, a row's words, steps and form, pure and tested), WalletSheet.tsx (the steps and their forms), AddWallet.tsx.
// Every round's rule is kept: a row's form is the screen's (by address id) and one form panel is mounted at a time; nothing is typed while a request
// is out; the owner alone approves a payout address; the payout stage (db/0098's `payout_stage`); the whole address on the phone; one gold button
// across open forms; counts are silent over a failed refresh.
//
// THEN THE OPERATOR'S RULING (26 Oct 2026: Astra's mockup is the default). Astra's register is ONE list — wallet / owner, network, address, status —
// with search first and the views behind Filters, Add wallet the one gold action, and a row opening its focused view (kit DetailSheet) where the steps
// and the reason wait until asked. So: the three sections are flattened into one list in their order (desk, payout, parties) and each row names its
// role; a party's Ledger account key is one row that opens its addresses; the About text, the Refresh button and the section bands are gone; Add
// wallet is a focused view of its own with a readback before Save.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Plus } from 'lucide-react';
import { api } from '../../lib/api';
import { readWords } from '../../lib/errors';
import { readState, offlineFailure } from '../../lib/read-state';
import { Pill } from '../../components/Pill';
import {
  Address, EmptyState, Notice, PageHead, Panel, Register, SearchBar, SkeletonRows, StateLine, TAP, type RegisterColumn, OfflineNotice } from '../../components/v2/kit';
import {
  SHOWS, chainsPresent, closeForm, editForm, failForm, filterRegister, goldCandidates, goldForm, openForm, registerRows, registerSections, rowFacts,
  scopeWords, showCounts, walletNameOf, foldName, addressCount, type Forms, type RegisterRow, type Show,
} from '../../components/v2/wallets/view';
import { ChainTag, KeySheet, PayoutPill, WalletSheet, type Ctx } from '../../components/v2/wallets/WalletSheet';
import { AddWallet } from '../../components/v2/wallets/AddWallet';
import { WaitingLine, waitingOf } from '../../components/v2/wallets/Waiting';


export function WalletsV2() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['wallets', 'register'], queryFn: () => api.walletRegister() });
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me() });
  const canChange = me.data?.role === 'full';
  // The server decides (owner only); the screen follows what it says, and offers it when it says nothing.
  const canApprovePayouts = me.data?.canApprovePayouts !== false;
  const [show, setShow] = useState<Show>('current');
  const [chain, setChain] = useState<string>('');
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  // The add form's request in flight: the view's fields and its way back are locked, here — the lock is the screen's (Astra, 26 Oct).
  const [addBusy, setAddBusy] = useState(false);
  const [forms, setForms] = useState<Forms>({});
  // The form the operator last opened or worked in: it owns the one gold button (design system §3).
  const [lastForm, setLastForm] = useState<string | null>(null);
  const [pending, setPendingRows] = useState<Record<string, true>>({});
  // The row whose focused view is open: an address id, or `key:<section>:<party>` for a Ledger account key's addresses.
  const [sheet, setSheet] = useState<string | null>(null);
  // What the last step reached beyond the Books — said here, not on its row: an archived row leaves the "In use" list the moment it is saved.
  const [notice, setNotice] = useState<string | null>(null);

  // A failed refresh keeps its old rows beside the error: they may stay, and no claim is drawn from them (counts, "nothing here").
  const rs = readState(q);
  const known = rs === 'known';
  const all = useMemo(() => q.data?.wallets ?? [], [q.data]);
  const counts = useMemo(() => showCounts(all), [all]);
  // A row with an open form stays in the list whatever the filters say: its draft, its request and its failure live in that form.
  const shown = useMemo(() => filterRegister(all, { show, chain, search }, forms), [all, show, chain, search, forms]);
  const rows = useMemo(() => registerRows(registerSections(shown)), [shown]);
  // The focused view finds its row in EVERYTHING read, not in the filtered list: a step that moves a row out of the view does not pull its sheet away.
  const allRows = useMemo(() => registerRows(registerSections(all)), [all]);
  const open = sheet ? (allRows.find((r) => r.key === sheet) ?? null) : null;
  const chains = useMemo(() => chainsPresent(all, chain), [all, chain]);
  const watchedCount = all.filter((w) => w.watched).length;
  const waiting = useMemo(() => waitingOf(all), [all]);
  // ?open=<id> (Custody's Needs you, 4 Nov 2026): that row's sheet, once the register is read; the parameter is then dropped.
  const [params, setParams] = useSearchParams();
  const openParam = params.get('open');
  useEffect(() => {
    if (!openParam || !known) return;
    if (all.some((w) => w.id === openParam)) { setLastForm(openParam); setSheet(openParam); }
    const next = new URLSearchParams(params);
    next.delete('open');
    setParams(next, { replace: true });
  }, [openParam, known]);   // eslint-disable-line react-hooks/exhaustive-deps
  const setPending = (id: string, on: boolean) => setPendingRows((p) => {
    const { [id]: _was, ...rest } = p;
    return on ? { ...rest, [id]: true as const } : rest;
  });
  // ONE GOLD across the forms that are ON SCREEN: the add view's, or the open sheet's row form (an Archive form is red and never gold). A row's form left
  // open behind a closed sheet keeps its draft and claims no gold. With none, Add wallet is the gold.
  const visible: Forms = open?.kind === 'wallet' && forms[open.w.id] ? { [open.w.id]: forms[open.w.id]! } : {};
  const gold = goldForm(goldCandidates(adding, adding ? {} : visible), lastForm);
  const closeSheet = useCallback(() => setSheet(null), []);
  const ctx: Ctx = {
    canChange, canApprovePayouts, forms, known, pending, setPending, gold, touch: setLastForm,
    onNotice: (words, id) => { setNotice(words); setSheet((s) => (s === id ? null : s)); },
    toggle: (w, step) => { setLastForm(w.id); setForms((f) => (f[w.id]?.step === step ? closeForm(f, w.id) : openForm(f, w, step))); },
    edit: (id, patch, live) => setForms((f) => editForm(f, id, patch, live)),
    fail: (id, words) => setForms((f) => failForm(f, id, words)),
    close: (id, step) => setForms((f) => closeForm(f, id, step)),
  };
  const openRow = (r: RegisterRow) => { setSheet(r.key); if (r.kind === 'wallet') setLastForm(r.w.id); };

  // ── ADD WALLET: a focused view of its own (Astra's wallet-add) ──
  if (adding) {
    return (
      <>
        <PageHead
          title="Add wallet"
          inline
          actions={<button type="button" className="btn-secondary min-h-11 px-4 text-body disabled:opacity-50 lg:min-h-10" disabled={addBusy} onClick={() => setAdding(false)}>Cancel</button>}
        />
        <AddWallet wallets={all} busy={addBusy} setBusy={setAddBusy} gold={gold === 'add'} touch={() => setLastForm('add')}
          onDone={(words) => { setAdding(false); setNotice(words); void qc.invalidateQueries({ queryKey: ['wallets'] }); }} />
      </>
    );
  }

  const active = (show !== 'current' ? 1 : 0) + (chain ? 1 : 0);
  const scope = scopeWords(all, { show, chain, search }, known);
  const pendingWord = (id: string) => (pending[id] ? 'Saving…' : forms[id] ? 'Draft' : null);

  // The status a row says: its state, and a payout address's stage; a request or a draft held in its form, quietly.
  const status = (r: RegisterRow) => {
    if (r.kind !== 'wallet') return null;
    const f = rowFacts(r.w);
    const held = pendingWord(r.w.id);
    return (
      // ONE STATE PILL A ROW (the audit, 1 Nov 2026: a green "In use" beside "Proposed" read as ready): a payout address in use
      // says its stage on the way to whitelisted; any other state (retired, flagged, archived) says that instead.
      <span className="inline-flex flex-wrap items-center justify-end gap-2">
        {held && <span className="text-meta text-tg-hint">{held}</span>}
        {f.payout && f.state.word === 'In use' ? <PayoutPill p={f.payout} day={false} /> : <Pill tone={f.state.tone}>{f.state.word}</Pill>}
      </span>
    );
  };
  // ONE NAME PER WALLET (N-091, 87dcf9a9): the register's own `name` (Group · Network · Tag) when the route sends it, else the
  // label as before (Astra, 1 Nov 2026: the rows said the retired forms). A folded row stands under its addresses' name too — never
  // "Ledger account key" or "Deposit · 1" (the CC, 3 Nov 2026); their count is the Address column's.
  const title = (r: RegisterRow) => (r.kind === 'wallet' && walletNameOf(r.w)) || (r.kind === 'key' ? foldName(r.rows) ?? 'Ledger account key'
    : r.kind === 'deposits' ? foldName(r.rows, true) ?? 'Deposit' : (r.w.label ?? `${r.w.chain} address`));
  const owner = (r: RegisterRow) => `${r.group.name} · ${r.role}`;

  const columns: RegisterColumn<RegisterRow>[] = [
    {
      key: 'wallet', head: 'Wallet / owner',
      cell: (r) => (
        <>
          {/* Two lines each, never cut at 1024 (the audit, 1 Nov 2026: fourteen owner lines cut alike). */}
          <span className="line-clamp-2 block break-words font-medium" title={title(r)}>{title(r)}</span>
          <span className="mt-1 line-clamp-2 block break-words text-meta text-tg-hint" title={owner(r)}>{owner(r)}</span>
          {r.kind === 'wallet' && rowFacts(r.w).note?.warn && <span className="mt-1 block"><Pill tone="warn">{rowFacts(r.w).note!.text}</Pill></span>}
        </>
      ),
    },
    { key: 'network', head: 'Network', width: 'w-[8rem]', cell: (r) => <span className="font-mono">{r.kind !== 'wallet' ? r.chains.join(' · ') : r.w.chain}</span> },
    // The laptop keeps the short form (first 8 · last 4), whole on hover and copied on a click; the phone's card shows it whole.
    { key: 'address', head: 'Address', width: 'w-[11rem]', cell: (r) => (r.kind !== 'wallet' ? <span className="text-meta text-tg-hint">{known ? addressCount(r.rows.length) : '— addresses'}</span> : <Address value={r.w.address} />) },
    { key: 'status', head: 'Status', align: 'right', width: 'w-[11rem]', cell: (r) => <span className="inline-flex items-center gap-2">{status(r)}<Chevron /></span> },
  ];

  const retry = <button type="button" className={TAP} disabled={q.isFetching} onClick={() => q.refetch()}>{q.isFetching ? 'Trying…' : 'Try again'}</button>;

  return (
    <>
      <PageHead
        title="Wallet register"
        inline
        sub={known ? <><span className="font-mono tabular">{all.length}</span> {all.length === 1 ? 'address' : 'addresses'} · <span className="font-mono tabular">{watchedCount}</span> watched</> : undefined}
        actions={
          <>
            {/* Refresh, back (the old register's; restored 1 Nov 2026) — the laptop's, as Energy's: the phone reads on its own. */}
            <span className="hidden lg:inline-flex">
              <button type="button" className="btn-secondary px-3 text-body disabled:opacity-50" disabled={q.isFetching} onClick={() => { void q.refetch(); }}>
                {q.isFetching ? 'Reading…' : 'Refresh'}
              </button>
            </span>
            {canChange && (
              <button type="button" className={`${gold === null ? 'btn-primary' : 'btn-secondary'} inline-flex min-h-11 items-center gap-2 px-4 text-body disabled:opacity-50 lg:min-h-10`}
                disabled={addBusy} onClick={() => { setSheet(null); setLastForm('add'); setAdding(true); }}>
                <Plus className="h-4 w-4" aria-hidden />Add wallet
              </button>
            )}
          </>
        }
      />

      {/* WAITING FOR HIS APPROVAL, first (4 Nov 2026): a tap opens the row's sheet, where Approve payout is. */}
      {known && waiting.length > 0 && (
        <Notice tone="warn"><WaitingLine waiting={waiting} onOpen={(w) => { setLastForm(w.id); setSheet(w.id); }} /></Notice>
      )}

      <SearchBar
        value={search}
        onChange={setSearch}
        placeholder="Party, name or address"
        active={active}
        filters={(
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Show">
              {SHOWS.map((s) => (
                <button key={s.key} type="button" aria-pressed={show === s.key} onClick={() => setShow(s.key)} className={`chip ${show === s.key ? 'chip-on' : ''}`}>
                  {s.label} <span className="font-mono tabular opacity-70">{known ? counts[s.key] : '—'}</span>
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-meta text-tg-hint">
                Network
                <select className="input w-auto" value={chain} onChange={(e) => setChain(e.target.value)}>
                  <option value="">All</option>
                  {chains.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              {(active > 0) && <button type="button" className={TAP} onClick={() => { setShow('current'); setChain(''); }}>Clear filters</button>}
            </div>
          </div>
        )}
      />

      {notice && <Notice tone="neutral" action={<button type="button" className={TAP} onClick={() => setNotice(null)}>Dismiss</button>}>{notice}</Notice>}

      {/* ONE FAILURE, ONE MESSAGE: a failed first read owns the page; a failed refresh says so above the rows it kept. */}
      {rs === 'failed' && offlineFailure(q.error) && <OfflineNotice what="the register" kept={!!q.data} />}
      {rs === 'failed' && !offlineFailure(q.error) && (
        <Notice tone="bad">
          <StateLine tone="bad" more={readWords(q.error)} action={retry}>
            {q.data ? <>Couldn&rsquo;t refresh — showing the last read.</> : <>Couldn&rsquo;t read the register.</>}
          </StateLine>
        </Notice>
      )}

      {rs === 'waiting' && !q.data ? (
        <Panel flush><SkeletonRows rows={5} what="the register" /></Panel>
      ) : rows.length > 0 ? (
        <Panel flush>
          <Register
            label="Wallet register"
            columns={columns}
            rows={rows}
            rowKey={(r) => r.key}
            onOpen={openRow}
            card={(r) => ({
              title: (
                <>
                  {/* One line each on the phone, the whole of both in the title and the opened record (Astra, 1 Nov 2026). */}
                  <span className="block truncate" title={title(r)}>{title(r)}</span>
                  <span className="mt-1 block truncate text-meta font-normal text-tg-hint" title={owner(r)}>{owner(r)}</span>
                  {r.kind === 'wallet' && (
                    // THE WHOLE ADDRESS on the phone (design system §5's one exception: the register exists to check one address
                    // against another) — one block, mono, wrapping evenly at one line height (Astra, 1 Nov 2026); the network is in the name.
                    <span className="mt-2 block break-all font-mono text-meta font-normal text-tg-hint">{r.w.address}</span>
                  )}
                  {r.kind === 'wallet' && rowFacts(r.w).note?.warn && <span className="mt-1 block font-normal"><Pill tone="warn">{rowFacts(r.w).note!.text}</Pill></span>}
                </>
              ),
              // The phone keeps the laptop's Network and Address columns (the audit, 1 Nov 2026: the cards dropped them).
              meta: r.kind !== 'wallet' ? `${r.chains.join(' · ')} · ${known ? addressCount(r.rows.length) : '— addresses'}` : r.w.chain,
              state: <span className="inline-flex items-center gap-2">{status(r)}<Chevron /></span>,
            })}
          />
          <div className="flex justify-between gap-3 border-t border-stroke-soft px-4 py-3 text-meta text-tg-hint lg:px-5">
            <span>{scope ?? ''}</span>
            <span>Dubai time</span>
          </div>
        </Panel>
      ) : known ? (
        // AN EMPTY CLAIM NEEDS A SUCCESSFUL READ (Appendix B).
        <EmptyState>{all.length === 0 ? 'No wallets yet.' : 'Nothing matches.'}</EmptyState>
      ) : null}

      {open?.kind === 'wallet' && <WalletSheet w={open.w} g={open.group} role={open.role} ctx={ctx} onClose={closeSheet} />}
      {open?.kind === 'key' && <KeySheet g={open.group} role={open.role} rows={open.rows} known={known} onClose={closeSheet} />}
      {open?.kind === 'deposits' && <KeySheet g={open.group} role={open.role} rows={open.rows} known={known} onClose={closeSheet} deposits />}
    </>
  );
}

function Chevron() {
  return <ChevronRight className="h-4 w-4 shrink-0 text-tg-hint" aria-hidden />;
}

